import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useCart } from '../contexts/CartContext';
import { ShoppingCart, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';
import { WhatsAppOrderButton } from '../components/WhatsAppOrderButton';

export const Products = () => {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(category || '');
  const { addToCart } = useCart();

  const search = searchParams.get('search') || '';
  const featured = searchParams.get('featured') === 'true';

  useEffect(() => {
    api.get('/categories').then(res => setCategories(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        let url = '/products?limit=50';
        if (category) url += `&category=${category}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        if (featured) url += `&featured=true`;
        const res = await api.get(url);
        setProducts(res.data.products);
      } catch (error) {
        toast.error('Failed to load products');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
    setSelectedCategory(category || '');
  }, [category, search, featured]);

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success(`${product.name} added to cart`);
  };

  const currentCategory = categories.find(c => c.slug === category);
  const seoTitle = search
    ? `Search: ${search}`
    : currentCategory
      ? `Buy ${currentCategory.name} Online`
      : 'All Products';
  const seoDesc = currentCategory
    ? (currentCategory.meta_description
       || `Buy ${currentCategory.name} online at Sanatanbhavan. ${currentCategory.description || ''} Free shipping above ₹500.`)
    : 'Browse the complete Sanatanbhavan catalogue of authentic puja samagri — diyas, kalash, idols, malas, hawan kund, festival kits and more.';

  // BreadcrumbList JSON-LD for Google's rich snippets
  const breadcrumbJsonLd = currentCategory
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://sanatanbhavan.com/' },
          { '@type': 'ListItem', position: 2, name: 'Products', item: 'https://sanatanbhavan.com/products' },
          { '@type': 'ListItem', position: 3, name: currentCategory.name, item: `https://sanatanbhavan.com/products/${currentCategory.slug}` },
        ],
      }
    : null;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <SEO title={seoTitle} description={seoDesc} jsonLd={breadcrumbJsonLd} />

      {/* Category landing hero — H1 + descriptive paragraph for SEO + UX */}
      {currentCategory && (
        <section
          data-testid="category-hero"
          className="mb-8 bg-gradient-to-br from-orange-50 via-amber-50 to-white border border-orange-100 rounded-2xl p-6 md:p-8"
        >
          <nav className="text-xs text-gray-500 mb-3" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-orange-700">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/products" className="hover:text-orange-700">Products</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-700">{currentCategory.name}</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-bold text-orange-900 leading-tight">
            Buy {currentCategory.name} Online
          </h1>
          {(currentCategory.long_description || currentCategory.description) && (
            <p data-testid="category-long-desc" className="text-gray-700 mt-3 leading-relaxed max-w-3xl">
              {currentCategory.long_description || currentCategory.description}
            </p>
          )}
          <div className="flex flex-wrap gap-2 mt-4 text-xs">
            <span className="bg-white/80 border border-orange-200 px-3 py-1 rounded-full text-orange-800">100% Authentic</span>
            <span className="bg-white/80 border border-orange-200 px-3 py-1 rounded-full text-orange-800">Free shipping ₹500+</span>
            <span className="bg-white/80 border border-orange-200 px-3 py-1 rounded-full text-orange-800">Secure UPI / Bank</span>
            <span className="bg-white/80 border border-orange-200 px-3 py-1 rounded-full text-orange-800">Pan-India delivery</span>
          </div>
        </section>
      )}

      <div className="flex flex-col md:flex-row gap-8">
        {/* Sidebar */}
        <aside className="md:w-64 flex-shrink-0">
          <div className="bg-white rounded-xl shadow-sm p-4 sticky top-24">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Filter size={18} /> Categories
            </h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/products"
                  className={`block px-3 py-2 rounded-lg transition ${!selectedCategory ? 'bg-orange-100 text-orange-700' : 'hover:bg-gray-100'}`}
                >
                  All Products
                </Link>
              </li>
              {categories.map(cat => (
                <li key={cat.id}>
                  <Link
                    to={`/products/${cat.slug}`}
                    className={`block px-3 py-2 rounded-lg transition ${selectedCategory === cat.slug ? 'bg-orange-100 text-orange-700' : 'hover:bg-gray-100'}`}
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Products Grid */}
        <main className="flex-1">
          <div className="mb-6">
            {!currentCategory && (
              <h1 className="text-3xl font-bold text-orange-800">
                {search ? `Search: "${search}"` : (featured ? 'Featured Products' : 'All Products')}
              </h1>
            )}
            <p className="text-sm text-gray-500 mt-2">{products.length} {products.length === 1 ? 'product' : 'products'} found</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl">
              <p className="text-gray-500">No products found</p>
              <Link to="/products" className="text-orange-600 hover:underline mt-2 inline-block">
                Browse all products
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.map((product) => (
                <Card key={product.id} className="group overflow-hidden" data-testid={`product-${product.id}`}>
                  <Link to={`/product/${product.id}`}>
                    <div className="aspect-square overflow-hidden bg-orange-50">
                      <img
                        src={product.images?.[0] || 'https://images.pexels.com/photos/5702290/pexels-photo-5702290.jpeg?w=400'}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition"
                      />
                    </div>
                  </Link>
                  <CardContent className="p-3">
                    <p className="text-xs text-orange-600 mb-1">{product.category_name}</p>
                    <Link to={`/product/${product.id}`}>
                      <h3 className="font-medium text-gray-800 hover:text-orange-600 text-sm line-clamp-2 min-h-[2.5rem]">
                        {product.name}
                      </h3>
                    </Link>
                    <div className="flex items-center justify-between mt-2">
                      <div>
                        <span className="font-bold text-orange-600">₹{product.sale_price || product.price}</span>
                        {product.sale_price && (
                          <span className="text-xs text-gray-400 line-through ml-1">₹{product.price}</span>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <WhatsAppOrderButton product={product} compact className="w-8 h-8" />
                        <Button
                          size="sm"
                          onClick={() => handleAddToCart(product)}
                          className="bg-orange-600 hover:bg-orange-700 h-8 w-8 p-0"
                          aria-label="Add to cart"
                        >
                          <ShoppingCart size={14} />
                        </Button>
                      </div>
                    </div>
                    {product.stock <= 5 && product.stock > 0 && (
                      <p className="text-xs text-red-500 mt-1">Only {product.stock} left!</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};
